/**
 * Client Intelligence — derived Client Hub metrics.
 *
 * All values are computed from source-of-truth tables (projects, invoices,
 * time_logs). Do not persist rollups on `clients`.
 */

import { supabase } from "../supabase";
import {
  getOutstandingBalance,
  enrichInvoiceForReceivables,
  isOverdueOpenInvoice,
} from "./accountsReceivable";
import { isOpenReceivableInvoice } from "./invoiceMoney";
import {
  isMissingInvoicePaymentsRelation,
  resolveInvoiceStatus,
} from "./invoices";
import { isProjectArchived } from "./projectVisibility";
import { applyOrganizationScopeForActor } from "./orgQueryScope";
import { getProjectsRelationName } from "./operationalProjections";
import { applyProjectVisibilityFilter } from "./teamScope";
import {
  applyOperationalTimeLogOrgScope,
  applyUserIdsToQuery,
  resolveOperationalOrganizationId,
  resolveScopedUserIdsForQuery,
  selectOperationalTimeLogs,
} from "./scopedQueries";
import { CAPABILITY, can as canCapability } from "./permissions";
import { shouldSkipSupabase } from "./guestMode";

function toHours(log) {
  const netSeconds = Number(log?.net_work_seconds);
  if (Number.isFinite(netSeconds) && netSeconds > 0) {
    return netSeconds / 3600;
  }
  const totalHours = Number(log?.total_hours);
  return Number.isFinite(totalHours) ? totalHours : 0;
}

function projectDisplayName(project) {
  return project?.project_name || "Unnamed Project";
}

function projectValue(project) {
  const budget = Number(project?.budget_amount);
  return Number.isFinite(budget) ? budget : null;
}

/**
 * Pure derivation of Client Intelligence snapshot from already-loaded rows.
 *
 * @param {{
 *   client?: object|null,
 *   projects?: object[],
 *   invoices?: object[],
 *   timeLogs?: object[],
 *   asOfDate?: Date,
 *   includeFinancial?: boolean,
 *   includeHours?: boolean,
 * }} input
 */
export function buildClientIntelligenceSnapshot({
  client = null,
  projects = [],
  invoices = [],
  timeLogs = [],
  asOfDate = new Date(),
  includeFinancial = true,
  includeHours = true,
} = {}) {
  const clientProjects = (projects || []).filter(Boolean);
  const activeProjects = clientProjects.filter((p) => !isProjectArchived(p));
  const completedProjects = clientProjects.filter((p) => isProjectArchived(p));

  const activeProjectValue = activeProjects.reduce((sum, project) => {
    const value = projectValue(project);
    return value == null ? sum : sum + value;
  }, 0);
  const hasProjectValues = activeProjects.some((p) => projectValue(p) != null);

  const projectRows = [...activeProjects, ...completedProjects].map((project) => ({
    projectId: project.project_id,
    name: projectDisplayName(project),
    status: isProjectArchived(project) ? "archived" : project.status || "active",
    isActive: !isProjectArchived(project),
    value: projectValue(project),
    projectNumber: project.project_number || null,
    siteAddress: project.site_address || null,
    updatedAt: project.updated_at || project.created_at || null,
    raw: project,
  }));

  let outstandingBalance = 0;
  let overdueAmount = 0;
  let openInvoiceCount = 0;
  let overdueInvoiceCount = 0;
  let paidInvoiceCount = 0;
  const openInvoices = [];
  const recentInvoices = [];

  if (includeFinancial) {
    const enriched = (invoices || []).map((invoice) =>
      enrichInvoiceForReceivables(invoice, asOfDate),
    );

    for (const invoice of enriched) {
      const status = resolveInvoiceStatus(invoice.status);
      const outstanding = getOutstandingBalance(invoice);
      if (isOpenReceivableInvoice(invoice)) {
        outstandingBalance += outstanding;
        openInvoiceCount += 1;
        openInvoices.push(invoice);
        if (isOverdueOpenInvoice(invoice, asOfDate)) {
          overdueAmount += outstanding;
          overdueInvoiceCount += 1;
        }
      } else if (status === "paid" || (status === "sent" && outstanding <= 0)) {
        paidInvoiceCount += 1;
      }
    }

    const openIds = new Set(openInvoices.map((invoice) => invoice.invoice_id));
    recentInvoices.push(
      ...[...enriched]
        .filter((invoice) => !openIds.has(invoice.invoice_id))
        .sort((a, b) => {
          const aTime = new Date(a.updated_at || a.sent_at || a.created_at || 0).getTime();
          const bTime = new Date(b.updated_at || b.sent_at || b.created_at || 0).getTime();
          return bTime - aTime;
        })
        .slice(0, 8),
    );
  }

  const accountUpToDate =
    includeFinancial && openInvoiceCount === 0 && (invoices || []).length >= 0;

  let totalHours = 0;
  if (includeHours) {
    totalHours = (timeLogs || []).reduce((sum, log) => sum + toHours(log), 0);
  }

  const activityCandidates = [];
  for (const project of projectRows) {
    if (project.updatedAt) {
      activityCandidates.push({
        type: "project",
        id: project.projectId,
        label: project.name,
        detail: project.isActive ? "Project updated" : "Project archived",
        at: project.updatedAt,
      });
    }
  }
  for (const invoice of recentInvoices) {
    const at = invoice.paid_at || invoice.sent_at || invoice.updated_at || invoice.created_at;
    if (!at) continue;
    activityCandidates.push({
      type: "invoice",
      id: invoice.invoice_id,
      label: invoice.invoice_number || "Invoice",
      detail: `Invoice ${resolveInvoiceStatus(invoice.status)}`,
      at,
      outstandingBalance: invoice.outstandingBalance,
    });
  }
  for (const log of timeLogs || []) {
    const at = log.date_iso || log.updated_at || log.created_at;
    if (!at) continue;
    activityCandidates.push({
      type: "time",
      id: log.id || `${log.project_id}-${log.date_iso}`,
      label: log.project_name || "Time log",
      detail: `${toHours(log).toFixed(1)} hours logged`,
      at,
    });
  }

  const recentActivity = activityCandidates
    .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime())
    .slice(0, 10);

  return {
    client,
    metrics: {
      activeProjectCount: activeProjects.length,
      completedProjectCount: completedProjects.length,
      activeProjectValue: hasProjectValues ? activeProjectValue : null,
      totalHours: includeHours ? totalHours : null,
      outstandingBalance: includeFinancial ? outstandingBalance : null,
      overdueAmount: includeFinancial ? overdueAmount : null,
      openInvoiceCount: includeFinancial ? openInvoiceCount : null,
      overdueInvoiceCount: includeFinancial ? overdueInvoiceCount : null,
      paidInvoiceCount: includeFinancial ? paidInvoiceCount : null,
      accountUpToDate: includeFinancial ? outstandingBalance <= 0 : null,
      hasInvoiceHistory: includeFinancial ? (invoices || []).length > 0 : false,
    },
    projects: {
      active: projectRows.filter((row) => row.isActive),
      completed: projectRows.filter((row) => !row.isActive),
      all: projectRows,
    },
    invoices: {
      open: openInvoices,
      recent: recentInvoices,
    },
    recentActivity,
    flags: {
      includeFinancial,
      includeHours,
      accountUpToDate: includeFinancial ? accountUpToDate && outstandingBalance <= 0 : false,
    },
  };
}

/**
 * Load org-scoped source rows and build a Client Intelligence snapshot.
 *
 * @param {object} actor
 * @param {string} clientId
 * @param {{
 *   client?: object|null,
 *   supabaseClient?: object,
 *   directReportIds?: string[],
 *   asOfDate?: Date,
 * }} [options]
 */
export async function loadClientIntelligence(
  actor,
  clientId,
  {
    client = null,
    supabaseClient = supabase,
    directReportIds = [],
    asOfDate = new Date(),
  } = {},
) {
  if (!actor || !clientId || shouldSkipSupabase(actor)) {
    return buildClientIntelligenceSnapshot({
      client,
      includeFinancial: false,
      includeHours: false,
    });
  }

  const includeFinancial = canCapability(actor, CAPABILITY.INVOICE_VIEW);
  const includeHours = canCapability(actor, CAPABILITY.TIMELOG_VIEW);
  const includeProjectFinancial = canCapability(
    actor,
    CAPABILITY.PROJECT_DETAIL_FINANCIAL,
  );

  const projectSelect = includeProjectFinancial
    ? "project_id, project_name, project_number, client_id, status, is_active, budget_amount, site_address, estimated_end_date, updated_at, created_at, organization_id"
    : "project_id, project_name, project_number, client_id, status, is_active, site_address, estimated_end_date, updated_at, created_at, organization_id";

  const relation = getProjectsRelationName(actor);
  let projectsQuery = supabaseClient
    .from(relation)
    .select(projectSelect)
    .eq("client_id", clientId)
    .order("updated_at", { ascending: false });

  projectsQuery = await applyOrganizationScopeForActor(
    projectsQuery,
    actor,
    supabaseClient,
    { tableName: "projects" },
  );
  projectsQuery = applyProjectVisibilityFilter(projectsQuery, actor, directReportIds);

  const { data: projects, error: projectsError } = await projectsQuery;
  if (projectsError) {
    throw projectsError;
  }

  let invoices = [];
  if (includeFinancial) {
    let invoicesQuery = supabaseClient
      .from("invoices")
      .select(
        "invoice_id, invoice_number, title, client_id, project_id, status, total_amount, sent_at, paid_at, due_date, payment_terms_days, updated_at, created_at, organization_id, invoice_payments(payment_id, amount, payment_date, payment_role, reversal_of_payment_id)",
      )
      .eq("client_id", clientId)
      .order("updated_at", { ascending: false });

    invoicesQuery = await applyOrganizationScopeForActor(
      invoicesQuery,
      actor,
      supabaseClient,
      { tableName: "invoices" },
    );

    let { data, error } = await invoicesQuery;
    if (isMissingInvoicePaymentsRelation(error)) {
      let fallback = supabaseClient
        .from("invoices")
        .select(
          "invoice_id, invoice_number, title, client_id, project_id, status, total_amount, sent_at, paid_at, due_date, payment_terms_days, updated_at, created_at, organization_id",
        )
        .eq("client_id", clientId)
        .order("updated_at", { ascending: false });
      fallback = await applyOrganizationScopeForActor(
        fallback,
        actor,
        supabaseClient,
        { tableName: "invoices" },
      );
      ({ data, error } = await fallback);
    }
    if (error) {
      throw error;
    }
    invoices = data || [];
  }

  let timeLogs = [];
  if (includeHours) {
    const projectIds = (projects || []).map((p) => p.project_id).filter(Boolean);
    if (projectIds.length > 0) {
      let timeLogsQuery = selectOperationalTimeLogs(
        supabaseClient,
        "id, project_id, project_name, date_iso, total_hours, net_work_seconds, updated_at, created_at, client_id",
      ).in("project_id", projectIds);

      const timelogUserIds = await resolveScopedUserIdsForQuery(
        supabaseClient,
        actor,
        CAPABILITY.TIMELOG_VIEW,
        { directReportIds },
      );
      const organizationId = await resolveOperationalOrganizationId(actor, supabaseClient, {
        directReportIds,
      });

      timeLogsQuery = applyUserIdsToQuery(timeLogsQuery, timelogUserIds);
      timeLogsQuery = applyOperationalTimeLogOrgScope(timeLogsQuery, organizationId);

      const { data, error } = await timeLogsQuery;
      if (error) {
        throw error;
      }
      timeLogs = data || [];
    }
  }

  return buildClientIntelligenceSnapshot({
    client,
    projects: projects || [],
    invoices,
    timeLogs,
    asOfDate,
    includeFinancial,
    includeHours,
  });
}

export function formatClientCurrency(amount) {
  const value = Number(amount || 0);
  return `$${value.toLocaleString("en-CA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function formatOpenInvoiceCountLabel(count) {
  const invoices = Math.max(0, Number(count) || 0);
  if (invoices === 0) return "No open invoices";
  if (invoices === 1) return "1 open invoice";
  return `${invoices} open invoices`;
}

export function formatClientHours(hours) {
  const value = Number(hours || 0);
  if (!Number.isFinite(value)) return "0.0";
  return value.toFixed(1);
}
